import { AxiosError } from 'axios';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { useParams } from 'react-router-dom';
import React, { createContext, useContext, useState } from 'react';
import { UseMutationResult, UseQueryResult, useQueryClient } from '@tanstack/react-query';

import { useAccountState } from 'contexts/account/account-context';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { getScenarioPostProcess } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-result-get';
import { PostProcessScenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-post-process.type';
import { PostProcessRequestInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-post-process-input.type';
import { usePostProcessMapper } from 'views/bluviu-nutrition/features/scenarios-v2/hooks/use-post-process-mapper';
import { getMortalityProfiles } from 'views/bluviu-nutrition/features/scenarios-v2/api/mortality-profiles-get';
import { MortalityProfile } from 'views/bluviu-nutrition/features/scenarios-v2/types/mortality-profile';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';
import { ScenarioSubTab } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-sub-tab.enum';

type ScenarioResultOverrides = Record<string, { program: string | null; isOverriding: boolean }>;

type ScenarioResultState = {
  selectedPrograms: string[];
  setSelectedPrograms: React.Dispatch<React.SetStateAction<string[]>>;
  editedFishGroups: Set<string>;
  setEditedFishGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  overrideFeedProgram: ScenarioResultOverrides;
  setOverrideFeedProgram: React.Dispatch<React.SetStateAction<ScenarioResultOverrides>>;
  selectedDefaultProgramName: string;
  setSelectedDefaultProgramName: React.Dispatch<React.SetStateAction<string>>;
  getScenarioPostProcessQuery: UseQueryResult<PostProcessScenario, AxiosError<unknown, any>>;
  updateScenarioPostProcessQuery: UseMutationResult<PostProcessScenario, AxiosError<unknown, any>, PostProcessRequestInput>;
  mortalityProfilesQuery: UseQueryResult<MortalityProfile[], AxiosError<unknown, any>>;
};

const ScenarioResultContext = createContext<ScenarioResultState | undefined>(undefined);

type ScenarioResultProviderProps = {
  children: React.ReactNode;
};

const ScenarioResultProvider: React.FC<ScenarioResultProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const { account: { company } } = useAccountState();
  const { buildPostProcessBody } = usePostProcessMapper();
  const { scenarioId, globalSiteId, subTab } = useParams<R2ScenarioParams>();
  const { r2ScenarioQuery: { data: scenario } } = useR2ScenarioState();

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [editedFishGroups, setEditedFishGroups] = useState<Set<string>>(new Set());
  const [selectedDefaultProgramName, setSelectedDefaultProgramName] = useState<string>('');
  const [overrideFeedProgram, setOverrideFeedProgram] = useState<ScenarioResultOverrides>({});

  const getScenarioPostProcessQuery = useGet<PostProcessScenario>(
    ['getScenarioPostProcess', scenarioId, globalSiteId],
    () => getScenarioPostProcess(scenarioId, buildPostProcessBody()),
    { enabled: !!scenario && scenario.state === ScenarioState.SCENARIO_RESULT_SENT && subTab === ScenarioSubTab.RESULTS }
  );

  const updateScenarioPostProcessQuery = usePost<PostProcessScenario, PostProcessRequestInput>(
    (body) => getScenarioPostProcess(scenarioId, body),
    (data) => {
      queryClient.setQueryData(['getScenarioPostProcess', scenarioId, globalSiteId], data);
    }
  );

  const mortalityProfilesQuery = useGet<MortalityProfile[]>(
    ['getMortalityProfiles', company?.marketId, company?.companyId, globalSiteId],
    () => getMortalityProfiles(String(company?.marketId), String(company?.companyId), String(globalSiteId)),
    { enabled: !!company?.marketId && !!company?.companyId && !!globalSiteId }
  );

  return (
    <ScenarioResultContext.Provider
      value={{
        editedFishGroups,
        setEditedFishGroups,
        overrideFeedProgram,
        setOverrideFeedProgram,
        selectedDefaultProgramName,
        setSelectedDefaultProgramName,
        getScenarioPostProcessQuery,
        updateScenarioPostProcessQuery,
        mortalityProfilesQuery,
        selectedPrograms,
        setSelectedPrograms,
      }}>
      {children}
    </ScenarioResultContext.Provider>
  );
};

function useScenarioResultState() {
  const context = useContext(ScenarioResultContext);
  if (!context) {
    throw new Error('useScenarioResultState must be used within a ScenarioResultProvider');
  }
  return context;
}

export { ScenarioResultProvider, useScenarioResultState };
